import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCSVStore, UploadStep } from '../stores/csvStore';
import { Location } from '../types/data/csv';
import { FileUploader } from '../components/csv/FileUploader';
import CSVPreview from '../components/csv/CSVPreview';
import MultiFileCSVPreview from '../components/csv/MultiFileCSVPreview';
import ValidationResults from '../components/csv/ValidationResults';
import SubmissionModeSelector from '../components/csv/SubmissionModeSelector';
import DateRangeSelector from '../components/csv/DateRangeSelector';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';
import { dataMappingService, CreateMappingRequest } from '../services/dataMappingService';
import categoryKPIService from '../services/categoryKPIService';
import { KPICategory } from '../services/kpiCategoryService';
import userSelectionService, { SubmitDataPreferences, SELECTION_TYPES } from '../services/userSelectionService';
import financialSubmissionService, { CreateFinancialSubmissionRequest } from '../services/financialSubmissionService';
import multiPeriodSubmissionService, { MultiPeriodSubmissionRequest } from '../services/multiPeriodSubmissionService';
import { processFinancialData, validateMappedCodesInCSV } from '../utils/financialDataProcessor';
import './SubmitDataPage.css';

// Map the enum steps to numeric indices for the UI
const UPLOAD_SUBMISSION = 0;
const MAP_SUBMISSION = 1;

const SubmitDataPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { 
    uploadedFile, 
    uploadedFiles,
    validationResults, 
    clearData, 
    validateCurrentFile, 
    currentStep, 
    setCurrentStep,
    accessibleLocations,
    selectedLocation,
    setAccessibleLocations,
    isMultiLocationMode,
    submissionMode,
    isMultiPeriodMode,
    getAllUniqueColumns,
    setUploadedFile,
    addUploadedFile
  } = useCSVStore();
  const [activeStep, setActiveStep] = useState<number>(UPLOAD_SUBMISSION);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [kpiCategories, setKpiCategories] = useState<KPICategory[]>([]);
  const [categoryKPIs, setCategoryKPIs] = useState<any[]>([]);
  const [loadingKPIs, setLoadingKPIs] = useState(false);
  const [kpisLoaded, setKpisLoaded] = useState(false);
  const [selectedIdentifierColumn, setSelectedIdentifierColumn] = useState<string>('');
  const [selectedDataColumn, setSelectedDataColumn] = useState<string>('');
  const [identifierMappings, setIdentifierMappings] = useState<{[key: string]: string}>({});
  const [selectedToggle, setSelectedToggle] = useState<string>('whole');
  const [selectedCategoryView, setSelectedCategoryView] = useState<any | null>(null);
  
  // Selection states for mapping
  const [selectedInputCode, setSelectedInputCode] = useState<string>('');
  const [selectedKpiCode, setSelectedKpiCode] = useState<string>('');
  const [creatingMapping, setCreatingMapping] = useState<boolean>(false);
  const [createdMappings, setCreatedMappings] = useState<{[kpiCode: string]: string[]}>({});
  const [showPercentModal, setShowPercentModal] = useState<boolean>(false);
  const [pendingMapping, setPendingMapping] = useState<{inputCode: string, kpiCode: string} | null>(null);
  const [percentValue, setPercentValue] = useState<string>('');
  const [percentMappings, setPercentMappings] = useState<{[inputCode: string]: number}>({});
  const [filterText, setFilterText] = useState<string>('');
  const [loadedMappings, setLoadedMappings] = useState<any[]>([]);
  const [aiMappingLoading, setAiMappingLoading] = useState<boolean>(false);
  const [showAiReviewModal, setShowAiReviewModal] = useState<boolean>(false);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<{[key: string]: boolean}>({});
  const [manualAutoMapLoading, setManualAutoMapLoading] = useState<boolean>(false);
  
  // Per-file column selections for AI mapping
  const [fileColumnSelections, setFileColumnSelections] = useState<{[fileName: string]: {identifierColumn: string, dataColumn: string}}>({});
  const [showFileColumnSelector, setShowFileColumnSelector] = useState<boolean>(false);

  // Fetch user's accessible locations on component mount
  useEffect(() => {
    fetchAccessibleLocations();
    loadExistingMappings();
  }, []);

  // Load existing mappings from the database
  const loadExistingMappings = async () => {
    try {
      const mappings = await dataMappingService.getDataMappings();
      const mappingDict: {[kpiCode: string]: string[]} = {};
      const percentDict: {[inputCode: string]: number} = {};
      
      mappings.forEach(mapping => {
        // Build KPI mapping dictionary
        if (!mappingDict[mapping.kpiCode]) {
          mappingDict[mapping.kpiCode] = [];
        }
        mappingDict[mapping.kpiCode].push(mapping.inputCode);
        
        // Build percent mapping dictionary
        if (mapping.mappingType === 'percent' && mapping.percentValue) {
          percentDict[mapping.inputCode] = (percentDict[mapping.inputCode] || 0) + mapping.percentValue;
        }
      });
      
      setCreatedMappings(mappingDict);
      setPercentMappings(percentDict);
      setLoadedMappings(mappings); // Store raw mappings for details
      console.log('Loaded existing mappings:', mappingDict);
      console.log('Loaded existing percent mappings:', percentDict);
    } catch (error) {
      console.error('Error loading existing mappings:', error);
    }
  };

  // Custom validation for Submit Data - accepts any CSV format
  const validateSubmitDataFile = () => {
    if (!uploadedFile) return;
    
    // Simple validation - just check that we have data
    const validationResults = {
      valid: true,
      errors: [],
      warnings: []
    };
    
    // Set the validation results directly without using the store's validation
    const { setValidationResults } = useCSVStore.getState();
    setValidationResults(validationResults);
  };

  // Load user preferences on component mount
  useEffect(() => {
    loadUserPreferences();
  }, []);

  // Reload preferences when a new file is uploaded to validate column selections
  useEffect(() => {
    if (uploadedFile) {
      loadUserPreferences();
    }
  }, [uploadedFile]);

  // Load user preferences from the backend
  const loadUserPreferences = async () => {
    try {
      const preferences = await userSelectionService.getSubmitDataPreferences();
      if (preferences) {
        // Apply saved preferences
        if (preferences.selectedYear !== undefined) {
          setSelectedYear(preferences.selectedYear);
        }
        if (preferences.selectedMonth !== undefined) {
          setSelectedMonth(preferences.selectedMonth);
        }
        
        // Only set column selections if they exist in the current CSV file
        if (preferences.selectedIdentifierColumn !== undefined && uploadedFile) {
          if (uploadedFile.columns.includes(preferences.selectedIdentifierColumn)) {
            setSelectedIdentifierColumn(preferences.selectedIdentifierColumn);
          } else {
            console.warn(`Saved identifier column "${preferences.selectedIdentifierColumn}" not found in CSV. Clearing selection.`);
            setSelectedIdentifierColumn('');
          }
        }
        if (preferences.selectedDataColumn !== undefined && uploadedFile) {
          if (uploadedFile.columns.includes(preferences.selectedDataColumn)) {
            setSelectedDataColumn(preferences.selectedDataColumn);
          } else {
            console.warn(`Saved data column "${preferences.selectedDataColumn}" not found in CSV. Clearing selection.`);
            setSelectedDataColumn('');
          }
        }
        
        if (preferences.selectedToggle !== undefined) {
          setSelectedToggle(preferences.selectedToggle);
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
      // Don't show error to user - just use defaults
    }
  };

  // Save user preferences to the backend
  const saveUserPreferences = async () => {
    try {
      const preferences: SubmitDataPreferences = {
        selectedYear,
        selectedMonth,
        selectedIdentifierColumn,
        selectedDataColumn,
        selectedToggle: selectedToggle as 'whole' | 'percent'
      };

      await userSelectionService.saveSubmitDataPreferences(preferences);
    } catch (error) {
      console.error('Error saving user preferences:', error);
      // Don't show error to user - preferences will just not be saved
    }
  };

  // Save preferences when key values change
  useEffect(() => {
    if (selectedIdentifierColumn || selectedDataColumn || selectedToggle !== 'whole') {
      // Only save if user has made some selections (avoid saving initial defaults)
      saveUserPreferences();
    }
  }, [selectedYear, selectedMonth, selectedIdentifierColumn, selectedDataColumn, selectedToggle]);

  // Validation is no longer needed as we removed the final step

  // Fetch KPIs when entering the mapping step
  useEffect(() => {
    if (activeStep === MAP_SUBMISSION && kpiCategories.length === 0) {
      fetchKPICategories();
    }
  }, [activeStep, kpiCategories.length]);

  // Reset KPI loaded state when leaving the mapping step
  useEffect(() => {
    if (activeStep !== MAP_SUBMISSION) {
      setKpisLoaded(false);
    }
  }, [activeStep]);

  const fetchAccessibleLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch accessible locations: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setAccessibleLocations(data.data);
      }
    } catch (error) {
      console.error('Error fetching accessible locations:', error);
      setAccessibleLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchKPICategories = async () => {
    try {
      setLoadingKPIs(true);
      setKpisLoaded(false);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/kpi-categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch KPI categories: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setKpiCategories(data.data);
        await fetchCategoryKPIs();
      }
    } catch (error) {
      console.error('Error fetching KPI categories:', error);
      setKpiCategories([]);
      setKpisLoaded(true); // Set to true even on error to stop loading state
    } finally {
      setLoadingKPIs(false);
    }
  };

  const fetchCategoryKPIs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/category-kpis/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch category KPIs: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setCategoryKPIs(data.data);
      }
    } catch (error) {
      console.error('Error fetching category KPIs:', error);
      setCategoryKPIs([]);
    } finally {
      setKpisLoaded(true); // Mark KPIs as loaded regardless of success/failure
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToNextStep = () => {
    if (activeStep < MAP_SUBMISSION) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (activeStep > UPLOAD_SUBMISSION) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const goToMapData = () => {
    navigate('/map-data');
  };

  const handleStartOver = () => {
    clearData();
    setActiveStep(UPLOAD_SUBMISSION);
  };

  const handleSubmissionSummary = () => {
    // TODO: Implement submission summary logic
    console.log('Submission summary clicked');
  };

  // Create a mapping with specific values (used for immediate mapping)
  const createMappingWithValues = async (inputCode: string, kpiCode: string, mappingType: 'whole' | 'percent', percentVal?: number) => {
    if (!inputCode || !kpiCode || !mappingType) {
      console.log('Missing requirements:', { inputCode, kpiCode, mappingType });
      return;
    }

    if (mappingType === 'percent' && !percentVal) {
      console.log('Percent value required for percent mapping');
      return;
    }

    setCreatingMapping(true);
    try {
      const mappingRequest: CreateMappingRequest = {
        inputCode,
        mappingType,
        kpiCode,
        ...(mappingType === 'percent' && { percentValue: percentVal })
      };

      const newMapping = await dataMappingService.createDataMapping(mappingRequest);
      console.log('Mapping created successfully:', newMapping);
      
      // Save the mapping to our local state for visual indicators
      setCreatedMappings(prev => ({
        ...prev,
        [kpiCode]: prev[kpiCode] ? [...prev[kpiCode], inputCode] : [inputCode]
      }));
      
      // Clear selections after successful mapping
      setSelectedInputCode('');
      setSelectedKpiCode('');
      
      // Refresh loaded mappings to get updated details
      loadExistingMappings();
      
      // TODO: Replace with better UI feedback
      console.log(`Successfully created ${mappingType} mapping: ${inputCode} → ${kpiCode}`);
      
    } catch (error: any) {
      console.error('Error creating mapping:', error);
      alert(`Failed to create mapping: ${error.message}`);
    } finally {
      setCreatingMapping(false);
    }
  };

  // Create a mapping between selected input code and KPI code (legacy function)
  const createMapping = async () => {
    if (!selectedInputCode || !selectedKpiCode || !selectedToggle) {
      console.log('Missing requirements:', { selectedInputCode, selectedKpiCode, selectedToggle });
      return;
    }

    await createMappingWithValues(selectedInputCode, selectedKpiCode, selectedToggle as 'whole' | 'percent');
  };

  // Handle input code selection from the left side data
  const handleInputCodeSelect = (inputCode: string) => {
    const newInputCode = inputCode === selectedInputCode ? '' : inputCode;
    setSelectedInputCode(newInputCode);
    
    // Auto-create mapping immediately if both sides are selected and toggle is active
    if (newInputCode && selectedKpiCode && selectedToggle) {
      if (selectedToggle === 'percent') {
        // For percent mappings, show modal to get percentage
        setPendingMapping({ inputCode: newInputCode, kpiCode: selectedKpiCode });
        setShowPercentModal(true);
      } else {
        // For whole mappings, create immediately
        createMappingWithValues(newInputCode, selectedKpiCode, selectedToggle as 'whole' | 'percent');
      }
    }
  };

  // Handle KPI code selection from the right side
  const handleKpiCodeSelect = (kpiCode: string) => {
    // If clicking on the same KPI, deselect it
    if (kpiCode === selectedKpiCode) {
      setSelectedKpiCode('');
      return;
    }
    
    setSelectedKpiCode(kpiCode);
    
    // If this KPI is already mapped, show current mappings in console for now
    if (createdMappings[kpiCode]) {
      console.log(`KPI ${kpiCode} is already mapped to: ${createdMappings[kpiCode].join(', ')}`);
    }
    
    // Auto-create mapping immediately if both sides are selected and toggle is active
    if (selectedInputCode && selectedToggle) {
      if (selectedToggle === 'percent') {
        // For percent mappings, show modal to get percentage
        setPendingMapping({ inputCode: selectedInputCode, kpiCode });
        setShowPercentModal(true);
      } else {
        // For whole mappings, create immediately
        createMappingWithValues(selectedInputCode, kpiCode, selectedToggle as 'whole' | 'percent');
      }
    }
  };

  const handleIdentifierMapping = (identifierValue: string, kpiCode: string) => {
    setIdentifierMappings(prev => ({
      ...prev,
      [identifierValue]: kpiCode
    }));
  };

  const handleCategoryClick = (category: any) => {
    setSelectedCategoryView(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategoryView(null);
  };

  // Handle percent modal confirmation
  const handlePercentConfirm = async () => {
    if (!pendingMapping || !percentValue) return;
    
    const percent = parseFloat(percentValue);
    const currentMapped = percentMappings[pendingMapping.inputCode] || 0;
    const remainingPercent = 100 - currentMapped;
    
    if (isNaN(percent) || percent <= 0) {
      alert('Please enter a valid percentage greater than 0');
      return;
    }
    
    if (percent > remainingPercent) {
      alert(`Cannot map ${percent}%. Only ${remainingPercent}% remaining for ${pendingMapping.inputCode}.`);
      return;
    }

    try {
      // Create the percent mapping
      await createMappingWithValues(
        pendingMapping.inputCode, 
        pendingMapping.kpiCode, 
        'percent',
        percent
      );
      
      // Update percent mappings tracking
      setPercentMappings(prev => ({
        ...prev,
        [pendingMapping.inputCode]: (prev[pendingMapping.inputCode] || 0) + percent
      }));
      
      // Close modal and reset
      setShowPercentModal(false);
      setPendingMapping(null);
      setPercentValue('');
      
    } catch (error) {
      console.error('Error creating percent mapping:', error);
    }
  };

  // Effect to manage body scroll when modal is open
  React.useEffect(() => {
    if (showPercentModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPercentModal]);

  // Handle percent modal cancel
  const handlePercentCancel = () => {
    setShowPercentModal(false);
    setPendingMapping(null);
    setPercentValue('');
    // Clear selections
    setSelectedInputCode('');
    setSelectedKpiCode('');
  };

  // Helper function to find which files contain a specific identifier
  const getFilesContainingIdentifier = (identifier: string): string[] => {
    if (uploadedFiles.length === 0) return [];
    
    return uploadedFiles
      .filter(file => {
        const identifierIndex = file.parsedData.headers.indexOf(selectedIdentifierColumn);
        if (identifierIndex === -1) return false;
        
        return file.parsedData.rows.some(row => 
          row[identifierIndex] && row[identifierIndex].toString().trim() === identifier
        );
      })
      .map(file => file.name);
  };

  // Delete a specific mapping
  // Handle AI mapping suggestions
  const handleAIMapping = async () => {
    // Check if we have multi-file or single file data
    const hasMultipleFiles = uploadedFiles.length > 0;
    const hasData = hasMultipleFiles || uploadedFile;
    
    if (!hasData || !kpiCategories.length) {
      alert('Please ensure you have uploaded data and have KPI categories loaded');
      return;
    }

    // For multi-file scenarios, check if we have per-file column selections
    if (hasMultipleFiles) {
      const missingSelections = uploadedFiles.filter(file => 
        !fileColumnSelections[file.name] || 
        !fileColumnSelections[file.name].identifierColumn || 
        !fileColumnSelections[file.name].dataColumn
      );
      
      if (missingSelections.length > 0) {
        alert(`Please select identifier and data columns for all files. Missing selections for: ${missingSelections.map(f => f.name).join(', ')}`);
        setShowFileColumnSelector(true);
        return;
      }
    } else {
      // Single file - use global column selections
      if (!selectedIdentifierColumn || !selectedDataColumn) {
        alert('Please select identifier and data columns');
        return;
      }
    }

    setAiMappingLoading(true);
    try {
      let inputData: any[] = [];
      let totalProcessedRows = 0;
      let totalFiles = 0;
      let allUniqueColumns = new Set<string>();

      if (hasMultipleFiles) {
        // Multi-file processing: use per-file column selections
        const seenIdentifiers = new Map<string, { value: string, sourceFile: string }>();
        
        uploadedFiles.forEach(file => {
          const fileSelection = fileColumnSelections[file.name];
          if (!fileSelection) return; // Skip files without column selections
          
          const identifierIndex = file.parsedData.headers.indexOf(fileSelection.identifierColumn);
          const dataIndex = file.parsedData.headers.indexOf(fileSelection.dataColumn);
          
          if (identifierIndex !== -1 && dataIndex !== -1) {
            totalFiles++;
            
            // Track all columns from this file
            file.parsedData.headers.forEach(header => allUniqueColumns.add(header));
            
            const fileInputData = file.parsedData.rows
              .filter(row => row[identifierIndex] && row[dataIndex]) // Filter out empty rows
              .map(row => {
                // Create complete column data object from this file
                const allColumns: { [columnName: string]: string } = {};
                file.parsedData.headers.forEach((columnName, index) => {
                  allColumns[columnName] = row[index]?.toString().trim() || '';
                });

                return {
                  identifier: row[identifierIndex].trim(),
                  value: row[dataIndex].trim(),
                  allColumns,
                  sourceFile: file.name
                };
              })
              .filter(item => item.identifier && item.value); // Remove empty items

            // Add to combined data, handling duplicates by keeping the first occurrence
            fileInputData.forEach(item => {
              if (!seenIdentifiers.has(item.identifier)) {
                seenIdentifiers.set(item.identifier, { value: item.value, sourceFile: item.sourceFile });
                inputData.push(item);
              }
            });
            
            totalProcessedRows += fileInputData.length;
          }
        });
        
        console.log(`🔄 Multi-File AI Mapping: Processed ${totalProcessedRows} rows from ${totalFiles} files → ${inputData.length} unique identifiers`);
        console.log(`📊 Multi-File AI Mapping: Combined ${allUniqueColumns.size} unique columns: [${Array.from(allUniqueColumns).join(', ')}]`);
        
      } else {
        // Single file processing (fallback)
        const identifierIndex = uploadedFile!.columns.indexOf(selectedIdentifierColumn);
        const dataIndex = uploadedFile!.columns.indexOf(selectedDataColumn);
        
        if (identifierIndex === -1 || dataIndex === -1) {
          alert('Selected columns not found in CSV data');
          return;
        }

        // Get all rows (not just preview) and create identifier-value pairs with complete row data
        const seenIdentifiers = new Map<string, string>();
        inputData = uploadedFile!.rows
          .filter(row => row[identifierIndex] && row[dataIndex]) // Filter out empty rows
          .map(row => {
            // Create complete column data object
            const allColumns: { [columnName: string]: string } = {};
            uploadedFile!.columns.forEach((columnName, index) => {
              allColumns[columnName] = row[index]?.toString().trim() || '';
            });

            return {
              identifier: row[identifierIndex].trim(),
              value: row[dataIndex].trim(),
              allColumns,
              sourceFile: 'Single File'
            };
          })
          .filter(item => item.identifier && item.value) // Remove empty items
          .filter(item => {
            // Remove duplicates - keep only first occurrence of each identifier
            if (seenIdentifiers.has(item.identifier)) {
              return false; // Skip duplicate
            }
            seenIdentifiers.set(item.identifier, item.value);
            return true;
          });

        totalProcessedRows = uploadedFile!.rows.filter(row => row[identifierIndex] && row[dataIndex]).length;
        console.log(`🔄 Single-File AI Mapping: Processed ${totalProcessedRows} rows → ${inputData.length} unique identifiers`);
        console.log(`📊 Single-File AI Mapping: Using ${uploadedFile!.columns.length} columns: [${uploadedFile!.columns.join(', ')}]`);
      }

      if (inputData.length === 0) {
        alert('No data available for AI mapping. Please ensure the selected identifier and data columns exist in at least one uploaded file.');
        return;
      }

      // Prepare KPI categories with detailed information
      const kpiCategoriesWithDetails = await Promise.all(
        kpiCategories
          .filter(category => category.id) // Only process categories with valid IDs
          .map(async (category) => {
            const categoryKPIs = await categoryKPIService.getCategoryKPIs(category.id!); // Non-null assertion since we filtered
            
            return {
              id: category.id!,
              name: category.category_name, // Use the correct property name from KPICategory interface
              kpis: categoryKPIs.map(kpi => ({
                code: kpi.kpiCode,
                name: kpi.kpiName,
                description: kpi.description || `${kpi.kpiName} - ${category.category_name}`
              }))
            };
          })
      );

      // Call AI mapping service
      const response = await dataMappingService.getAIMappingSuggestions({
        inputData,
        kpiCategories: kpiCategoriesWithDetails
      });

      if (response.success && response.data.suggestedMappings.length > 0) {
        setAiSuggestions(response.data.suggestedMappings);
        
        // Pre-select all suggestions by default
        const defaultSelections: {[key: string]: boolean} = {};
        response.data.suggestedMappings.forEach((suggestion, index) => {
          defaultSelections[`${suggestion.inputCode}-${suggestion.suggestedKpiCode}-${index}`] = true;
        });
        setSelectedSuggestions(defaultSelections);
        
        setShowAiReviewModal(true);
      } else {
        alert(response.message || 'No AI mapping suggestions were generated. The AI could not find confident matches for your data.');
      }

    } catch (error: any) {
      console.error('Error generating AI mapping suggestions:', error);
      alert(`Failed to generate AI mapping suggestions: ${error.message}`);
    } finally {
      setAiMappingLoading(false);
    }
  };

  // Handle Manual Auto Map - direct 1:1 mapping of KPI codes
  const handleManualAutoMap = async () => {
    const hasMultipleFiles = uploadedFiles.length > 0;
    const hasData = hasMultipleFiles || uploadedFile;
    
    if (!hasData || !kpiCategories.length) {
      alert('Please ensure you have uploaded data and have KPI categories loaded');
      return;
    }

    // Check if identifier column is selected
    if (hasMultipleFiles) {
      const missingSelections = uploadedFiles.filter(file => 
        !fileColumnSelections[file.name] || 
        !fileColumnSelections[file.name].identifierColumn
      );
      
      if (missingSelections.length > 0) {
        alert(`Please select identifier column for all files. Missing selections for: ${missingSelections.map(f => f.name).join(', ')}`);
        setShowFileColumnSelector(true);
        return;
      }
    } else {
      if (!selectedIdentifierColumn) {
        alert('Please select an identifier column');
        return;
      }
    }

    setManualAutoMapLoading(true);
    try {
      // Get all unique identifiers from the CSV
      const uniqueIdentifiers = new Set<string>();
      
      if (hasMultipleFiles) {
        uploadedFiles.forEach(file => {
          const fileSelection = fileColumnSelections[file.name];
          if (!fileSelection) return;
          
          const identifierIndex = file.parsedData.headers.indexOf(fileSelection.identifierColumn);
          if (identifierIndex !== -1) {
            file.parsedData.rows.forEach(row => {
              const identifier = row[identifierIndex]?.toString().trim();
              if (identifier) {
                uniqueIdentifiers.add(identifier);
              }
            });
          }
        });
      } else {
        const identifierIndex = uploadedFile!.columns.indexOf(selectedIdentifierColumn);
        if (identifierIndex === -1) {
          alert('Selected identifier column not found in CSV data');
          return;
        }
        
        uploadedFile!.rows.forEach(row => {
          const identifier = row[identifierIndex]?.toString().trim();
          if (identifier) {
            uniqueIdentifiers.add(identifier);
          }
        });
      }

      // Get all KPI codes from the database
      const allKpiCodes = new Set(categoryKPIs.map(kpi => kpi.kpiCode));
      
      // Find matches (direct 1:1 mapping)
      const matches: Array<{inputCode: string, kpiCode: string}> = [];
      const alreadyMapped: string[] = [];
      const noMatch: string[] = [];
      
      uniqueIdentifiers.forEach(identifier => {
        if (allKpiCodes.has(identifier)) {
          // Check if this identifier is already mapped
          const existingMapping = Object.values(createdMappings).flat().includes(identifier);
          if (existingMapping) {
            alreadyMapped.push(identifier);
          } else {
            matches.push({
              inputCode: identifier,
              kpiCode: identifier  // Direct 1:1 match
            });
          }
        } else {
          noMatch.push(identifier);
        }
      });

      if (matches.length === 0) {
        alert(`No direct matches found.\n\n${alreadyMapped.length > 0 ? `Already mapped: ${alreadyMapped.length}\n` : ''}No match: ${noMatch.length}\n\nNote: This feature requires exact KPI code matches between your CSV and database.`);
        return;
      }

      // Create bulk mappings
      const bulkRequest = {
        mappings: matches.map(match => ({
          inputCode: match.inputCode,
          mappingType: 'whole' as const,
          kpiCode: match.kpiCode
        }))
      };

      await dataMappingService.createBulkMappings(bulkRequest);
      
      // Refresh mappings
      await loadExistingMappings();
      
      // Show summary
      const summary = `✅ Successfully created ${matches.length} direct mappings!\n\n` +
        `📊 Total identifiers: ${uniqueIdentifiers.size}\n` +
        `✅ Mapped: ${matches.length}\n` +
        `${alreadyMapped.length > 0 ? `⚠️ Already mapped: ${alreadyMapped.length}\n` : ''}` +
        `${noMatch.length > 0 ? `❌ No match: ${noMatch.length}` : ''}`;
      
      alert(summary);
      
    } catch (error: any) {
      console.error('Error during manual auto map:', error);
      alert(`Failed to create manual auto mappings: ${error.message}`);
    } finally {
      setManualAutoMapLoading(false);
    }
  };

  // Handle creating AI mappings after user review
  const handleCreateAIMappings = async () => {
    const mappingsToCreate = aiSuggestions.filter((suggestion, index) => 
      selectedSuggestions[`${suggestion.inputCode}-${suggestion.suggestedKpiCode}-${index}`]
    );

    if (mappingsToCreate.length === 0) {
      alert('Please select at least one mapping to create');
      return;
    }

    try {
      // Create bulk mappings
      const bulkRequest = {
        mappings: mappingsToCreate.map(suggestion => ({
          inputCode: suggestion.inputCode,
          mappingType: 'whole' as const,
          kpiCode: suggestion.suggestedKpiCode
        }))
      };

      await dataMappingService.createBulkMappings(bulkRequest);
      
      // Refresh mappings and preview data synchronously
      await loadExistingMappings();
      
      // Close modal and reset
      setShowAiReviewModal(false);
      setAiSuggestions([]);
      setSelectedSuggestions({});
      
      alert(`Successfully created ${mappingsToCreate.length} AI-suggested mappings!`);
      
    } catch (error: any) {
      console.error('Error creating AI mappings:', error);
      alert(`Failed to create AI mappings: ${error.message}`);
    }
  };

  // Handle AI review modal close
  const handleAiReviewClose = () => {
    setShowAiReviewModal(false);
    setAiSuggestions([]);
    setSelectedSuggestions({});
  };

  // Toggle suggestion selection
  const toggleSuggestionSelection = (suggestionKey: string) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [suggestionKey]: !prev[suggestionKey]
    }));
  };

  // Helper function to get KPI name from KPI code
  const getKpiNameFromCode = (kpiCode: string): string => {
    const kpi = categoryKPIs.find(kpi => kpi.kpiCode === kpiCode);
    return kpi ? kpi.kpiName : kpiCode; // Fallback to code if name not found
  };

  // Check if all KPIs in a category have mappings
  const isCategoryFullyMapped = (categoryId: number) => {
    const kpisInCategory = categoryKPIs.filter(kpi => kpi.categoryId === categoryId);
    if (kpisInCategory.length === 0) return false;
    
    return kpisInCategory.every(kpi => {
      return createdMappings[kpi.kpiCode] && createdMappings[kpi.kpiCode].length > 0;
    });
  };

  // Check if ALL categories are fully mapped
  const areAllCategoriesFullyMapped = () => {
    if (kpiCategories.length === 0 || categoryKPIs.length === 0) return false;
    
    return kpiCategories.every(category => isCategoryFullyMapped(category.id!));
  };

  // Validate that all mapped input codes exist in the uploaded files
  const validateMappedCodesExistInFile = (): { isValid: boolean; missingCodes: string[] } => {
    // Handle multi-file scenario
    if (uploadedFiles.length > 0) {
      // Get all mapped input codes from current mappings
      const mappedInputCodes = new Set<string>();
      
      // Add codes from whole mappings (createdMappings)
      Object.values(createdMappings).forEach(inputCodes => {
        inputCodes.forEach(code => mappedInputCodes.add(code));
      });

      // Add codes from percent mappings
      Object.keys(percentMappings).forEach(code => {
        mappedInputCodes.add(code);
      });

      // Get all input codes from ALL uploaded files using their respective column selections
      const allFileInputCodes = new Set<string>();
      
      uploadedFiles.forEach(file => {
        const fileSelection = fileColumnSelections[file.name];
        if (fileSelection && fileSelection.identifierColumn) {
          const identifierIndex = file.parsedData.headers.indexOf(fileSelection.identifierColumn);
          if (identifierIndex !== -1) {
            file.parsedData.rows.forEach(row => {
              const code = row[identifierIndex]?.toString().trim();
              if (code && code !== '') {
                allFileInputCodes.add(code);
              }
            });
          }
        }
      });

      // Find missing codes
      const missingCodes = Array.from(mappedInputCodes).filter(
        mappedCode => !allFileInputCodes.has(mappedCode)
      );

      return {
        isValid: missingCodes.length === 0,
        missingCodes
      };
    }

    // Fallback to single file validation
    if (!uploadedFile || !selectedIdentifierColumn) {
      return { isValid: true, missingCodes: [] };
    }

    // Get the column index for the identifier column
    const identifierIndex = uploadedFile.columns.indexOf(selectedIdentifierColumn);
    if (identifierIndex === -1) {
      return { isValid: false, missingCodes: ['Selected identifier column not found in file'] };
    }

    // Get all mapped input codes from current mappings
    const mappedInputCodes = new Set<string>();
    
    // Add codes from whole mappings (createdMappings)
    Object.values(createdMappings).forEach(inputCodes => {
      inputCodes.forEach(code => mappedInputCodes.add(code));
    });

    // Add codes from percent mappings
    Object.keys(percentMappings).forEach(code => {
      mappedInputCodes.add(code);
    });

    // Get all input codes from the uploaded file
    const fileInputCodes = new Set(
      uploadedFile.rows
        .map(row => row[identifierIndex]?.toString().trim())
        .filter(code => code && code !== '') // remove empty values
    );

    // Find missing codes
    const missingCodes = Array.from(mappedInputCodes).filter(
      mappedCode => !fileInputCodes.has(mappedCode)
    );

    return {
      isValid: missingCodes.length === 0,
      missingCodes
    };
  };

  // Create missing KPI entries with value 0 in the uploaded data
  const createMissingKPIsWithZeroValue = async (missingCodes: string[]) => {
    if (missingCodes.length === 0) return;

    // Handle multi-file scenario
    if (uploadedFiles.length > 0) {
      // For multi-file uploads, we need to add missing KPI entries to each file
      const updatedFiles = [...uploadedFiles];
      
      for (let fileIndex = 0; fileIndex < updatedFiles.length; fileIndex++) {
        const file = updatedFiles[fileIndex];
        const fileSelection = fileColumnSelections[file.name];
        
        if (fileSelection && fileSelection.identifierColumn && fileSelection.dataColumn) {
          const identifierIndex = file.parsedData.headers.indexOf(fileSelection.identifierColumn);
          const dataIndex = file.parsedData.headers.indexOf(fileSelection.dataColumn);
          
          if (identifierIndex !== -1 && dataIndex !== -1) {
            // Add missing codes as new rows with value 0
            const newRows = [...file.parsedData.rows];
            
            missingCodes.forEach(missingCode => {
              // Check if this code already exists in this file
              const existingRow = newRows.find(row => row[identifierIndex] === missingCode);
              if (!existingRow) {
                // Create new row with missing code and value 0
                const newRow = new Array(file.parsedData.headers.length).fill('');
                newRow[identifierIndex] = missingCode;
                newRow[dataIndex] = '0';
                
                // Fill in other columns with default values if needed
                const dealerIndex = file.parsedData.headers.findIndex(h => h.toLowerCase().includes('dealer'));
                const yearIndex = file.parsedData.headers.findIndex(h => h.toLowerCase().includes('year'));
                const monthIndex = file.parsedData.headers.findIndex(h => h.toLowerCase().includes('month'));
                
                if (dealerIndex !== -1 && newRows.length > 0) {
                  newRow[dealerIndex] = newRows[0][dealerIndex] || 'Default Dealer';
                }
                if (yearIndex !== -1 && newRows.length > 0) {
                  newRow[yearIndex] = newRows[0][yearIndex] || new Date().getFullYear().toString();
                }
                if (monthIndex !== -1 && newRows.length > 0) {
                  newRow[monthIndex] = newRows[0][monthIndex] || new Date().getMonth().toString();
                }
                
                newRows.push(newRow);
                console.log(`📝 Added missing KPI "${missingCode}" with value 0 to file ${file.name}`);
              }
            });
            
            // Update the file data
            updatedFiles[fileIndex] = {
              ...file,
              parsedData: {
                ...file.parsedData,
                rows: newRows
              }
            };
          }
        }
      }
      
      // Update the uploaded files state by re-adding each updated file
      updatedFiles.forEach(updatedFile => {
        addUploadedFile(updatedFile);
      });
      
    } else if (uploadedFile && selectedIdentifierColumn && selectedDataColumn) {
      // Handle single file scenario
      const identifierIndex = uploadedFile.columns.indexOf(selectedIdentifierColumn);
      const dataIndex = uploadedFile.columns.indexOf(selectedDataColumn);
      
      if (identifierIndex !== -1 && dataIndex !== -1) {
        const newRows = [...uploadedFile.rows];
        
        missingCodes.forEach(missingCode => {
          // Check if this code already exists
          const existingRow = newRows.find(row => row[identifierIndex] === missingCode);
          if (!existingRow) {
            // Create new row with missing code and value 0
            const newRow = new Array(uploadedFile.columns.length).fill('');
            newRow[identifierIndex] = missingCode;
            newRow[dataIndex] = '0';
            
            // Fill in other columns with default values from first row
            const dealerIndex = uploadedFile.columns.findIndex(h => h.toLowerCase().includes('dealer'));
            const yearIndex = uploadedFile.columns.findIndex(h => h.toLowerCase().includes('year'));
            const monthIndex = uploadedFile.columns.findIndex(h => h.toLowerCase().includes('month'));
            
            if (dealerIndex !== -1 && newRows.length > 0) {
              newRow[dealerIndex] = newRows[0][dealerIndex] || 'Default Dealer';
            }
            if (yearIndex !== -1 && newRows.length > 0) {
              newRow[yearIndex] = newRows[0][yearIndex] || new Date().getFullYear().toString();
            }
            if (monthIndex !== -1 && newRows.length > 0) {
              newRow[monthIndex] = newRows[0][monthIndex] || new Date().getMonth().toString();
            }
            
            newRows.push(newRow);
            console.log(`📝 Added missing KPI "${missingCode}" with value 0`);
          }
        });
        
        // Update the uploaded file
        setUploadedFile({
          ...uploadedFile,
          rows: newRows
        });
      }
    }
  };

  // Handle Submit Data button click
  const handleSubmitData = async () => {
    try {
      // Validate that all mapped codes exist in the file
      const validation = validateMappedCodesExistInFile();
      
      if (!validation.isValid) {
        // Instead of failing, create missing KPIs with value 0
        console.log(`⚠️ Missing codes detected: ${validation.missingCodes.join(', ')}`);
        console.log(`🔧 Creating missing KPIs with value 0...`);
        
        // Create missing KPI entries with value 0 in the uploaded data
        await createMissingKPIsWithZeroValue(validation.missingCodes);
        
        console.log(`✅ Successfully created ${validation.missingCodes.length} missing KPIs with value 0`);
      }

      // Check if we have data and user
      if (!user) {
        alert('❌ User not authenticated');
        return;
      }

      // Check for multi-file or single-file data
      const hasMultiFileData = uploadedFiles.length > 0;
      const hasSingleFileData = uploadedFile && selectedIdentifierColumn && selectedDataColumn;
      
      if (!hasMultiFileData && !hasSingleFileData) {
        alert('❌ Missing required data for submission. Please upload files and configure columns.');
        return;
      }

      // For multi-file, ensure all files have column selections
      if (hasMultiFileData) {
        const missingColumnSelections = uploadedFiles.filter(file => 
          !fileColumnSelections[file.name] || 
          !fileColumnSelections[file.name].identifierColumn || 
          !fileColumnSelections[file.name].dataColumn
        );
        
        if (missingColumnSelections.length > 0) {
          alert(`❌ Missing column selections for files: ${missingColumnSelections.map(f => f.name).join(', ')}`);
          return;
        }
      }

      // Load existing mappings from backend if no mappings are loaded
      let mappingData = loadedMappings.map(mapping => ({
        kpiCode: mapping.kpiCode,
        inputCode: mapping.inputCode,
        mappingType: mapping.mappingType as 'whole' | 'percent',
        percentValue: mapping.percentValue
      }));

      console.log(`🔍 Initial loadedMappings count: ${loadedMappings.length}`);
      console.log(`🔍 Initial mappingData count: ${mappingData.length}`);

      // If no mappings are loaded, fetch existing mappings from backend
      if (mappingData.length === 0) {
        console.log('🔄 No mappings loaded, fetching existing mappings from backend...');
        
        try {
          const existingMappings = await dataMappingService.getDataMappings();
          console.log('🔍 Raw existingMappings response:', existingMappings);
          
          if (existingMappings && existingMappings.length > 0) {
            console.log(`🔍 Found ${existingMappings.length} existing mappings:`, existingMappings);
            mappingData = existingMappings.map((mapping: any) => ({
              kpiCode: mapping.kpiCode,
              inputCode: mapping.inputCode,
              mappingType: mapping.mappingType as 'whole' | 'percent',
              percentValue: mapping.percentValue
            }));
            console.log(`✅ Loaded ${mappingData.length} existing mappings from backend`);
            console.log('🔍 Processed mappingData:', mappingData.slice(0, 3)); // Show first 3 mappings
          } else {
            console.warn('⚠️ No existing mappings found in backend');
            console.log('🔍 existingMappings is:', typeof existingMappings, existingMappings);
            
            // As a fallback, create identity mappings for KPI codes found in the CSV
            if (uploadedFile && uploadedFile.columns.includes('KPI Code')) {
              console.log('🔄 Creating identity mappings for KPI codes found in CSV...');
              const kpiCodeColumn = uploadedFile.columns.indexOf('KPI Code');
              const allKpiCodes = uploadedFile.rows.map(row => row[kpiCodeColumn]).filter(code => code);
              const uniqueKpiCodes = allKpiCodes.filter((code, index, self) => self.indexOf(code) === index);
              
              mappingData = uniqueKpiCodes.map(kpiCode => ({
                kpiCode: kpiCode,
                inputCode: kpiCode,
                mappingType: 'whole' as 'whole' | 'percent',
                percentValue: undefined
              }));
              
              console.log(`✅ Created ${mappingData.length} identity mappings for KPI codes:`, uniqueKpiCodes.slice(0, 5));
            }
          }
        } catch (error) {
          console.error('❌ Error fetching existing mappings:', error);
        }
      }

      // Ensure we have mappings before proceeding
      if (mappingData.length === 0) {
        alert('❌ No field mappings found. Please create mappings first or ensure existing mappings exist in the system.');
        return;
      }

      console.log(`📋 Using ${mappingData.length} mappings for submission`);

      // Create a CSV file from the uploaded data (multi-file or single-file)
      let csvContent: string;
      
      if (hasMultiFileData) {
        // Combine data from all uploaded files using a normalized structure
        const allRows: string[][] = [];
        
        // Use a standard header structure that works with the backend row-based processing
        const standardHeaders = ['Dealer', 'Year', 'Month', 'KPI Code', 'Value', 'Description'];
        allRows.push(standardHeaders);
        
        // Process each file and normalize its data to the standard structure
        uploadedFiles.forEach(file => {
          const fileHeaders = file.parsedData.headers;
          
          // Find the column indices for this file
          const dealerIndex = fileHeaders.findIndex(h => h.toLowerCase().includes('dealer'));
          const yearIndex = fileHeaders.findIndex(h => h.toLowerCase().includes('year'));
          const monthIndex = fileHeaders.findIndex(h => h.toLowerCase().includes('month'));
          const kpiCodeIndex = fileHeaders.findIndex(h => h.toLowerCase().includes('kpi') && h.toLowerCase().includes('code'));
          const valueIndex = fileHeaders.findIndex(h => h.toLowerCase().includes('value'));
          const descriptionIndex = fileHeaders.findIndex(h => h.toLowerCase().includes('description'));
          
          file.parsedData.rows.forEach(row => {
            // Create a normalized row with standard column structure
            const normalizedRow = [
              dealerIndex >= 0 ? (row[dealerIndex] || '') : '',
              yearIndex >= 0 ? (row[yearIndex] || '') : '',
              monthIndex >= 0 ? (row[monthIndex] || '') : '',
              kpiCodeIndex >= 0 ? (row[kpiCodeIndex] || '') : '',
              valueIndex >= 0 ? (row[valueIndex] || '') : '',
              descriptionIndex >= 0 ? (row[descriptionIndex] || '') : ''
            ];
            
            // Only add rows that have KPI data
            if (normalizedRow[3] && normalizedRow[4]) { // KPI Code and Value must exist
              allRows.push(normalizedRow);
            }
          });
        });
        
        csvContent = allRows.map(row => row.join(',')).join('\n');
      } else {
        // Single file approach
        csvContent = [
          uploadedFile!.columns.join(','),
          ...uploadedFile!.rows.map(row => row.join(','))
        ].join('\n');
      }

      const csvBlob = new Blob([csvContent], { type: 'text/csv' });
      const csvFile = new File([csvBlob], 'submission.csv', { type: 'text/csv' });

      // Prepare submission request for the new multi-period service
      const submissionRequest: MultiPeriodSubmissionRequest = {
        csvFile: csvFile,
        submissionMode: submissionMode,
        mappings: mappingData,
        // Single-period specific fields
        year: submissionMode === 'single-period' ? selectedYear : undefined,
        month: submissionMode === 'single-period' ? selectedMonth : undefined,
        locationId: submissionMode === 'single-period' && !isMultiLocationMode ? selectedLocation?.id : undefined,
        // Column selections for CSV processing
        identifierColumn: selectedIdentifierColumn,
        dataColumn: selectedDataColumn
      };

      console.log(`🔄 Submitting data in ${submissionMode} mode...`);
      console.log(`🔍 Final mappingData being sent (${mappingData.length} items):`, mappingData);
      console.log(`🔍 Submission request:`, {
        submissionMode: submissionRequest.submissionMode,
        mappingsCount: submissionRequest.mappings.length,
        year: submissionRequest.year,
        month: submissionRequest.month,
        locationId: submissionRequest.locationId
      });

      // Submit using the new multi-period service
      const result = await multiPeriodSubmissionService.submitMultiPeriodData(submissionRequest);

      if (result.success) {
        const successMessage = `✅ Financial data submitted successfully!\n` +
          `• Total submissions: ${result.data.totalSubmissions}\n` +
          `• Successful: ${result.data.successfulSubmissions}\n` +
          `• Failed: ${result.data.failedSubmissions}\n` +
          `• Duplicates skipped: ${result.data.duplicates.length}`;
        
        alert(successMessage);
        
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        alert(`❌ Submission failed: ${result.message}`);
      }

    } catch (error: any) {
      console.error('Error submitting financial data:', error);
      alert(`❌ Error submitting data: ${error.message}`);
    }
  };

  const handleDeleteMapping = async (kpiCode: string, inputCode: string) => {
    try {
      // Find the mapping ID from the database
      const mappings = await dataMappingService.getDataMappings();
      const mappingToDelete = mappings.find(m => m.kpiCode === kpiCode && m.inputCode === inputCode);
      
      if (!mappingToDelete) {
        console.error('Mapping not found for deletion');
        return;
      }

      // Delete from database
      await dataMappingService.deleteDataMapping(mappingToDelete.id!);
      
      // Update local mapping state
      setCreatedMappings(prev => {
        const updated = { ...prev };
        if (updated[kpiCode]) {
          updated[kpiCode] = updated[kpiCode].filter(code => code !== inputCode);
          // If no more mappings for this KPI, remove the key
          if (updated[kpiCode].length === 0) {
            delete updated[kpiCode];
          }
        }
        return updated;
      });

      // Update percent mappings state if this was a percent mapping
      if (mappingToDelete.mappingType === 'percent' && mappingToDelete.percentValue) {
        setPercentMappings(prev => {
          const updated = { ...prev };
          const currentPercent = updated[inputCode] || 0;
          const newPercent = currentPercent - mappingToDelete.percentValue!;
          
          if (newPercent <= 0) {
            delete updated[inputCode];
          } else {
            updated[inputCode] = newPercent;
          }
          return updated;
        });
      }

      // Refresh all mapping states to update the preview
      await loadExistingMappings();

      console.log(`Successfully deleted mapping: ${inputCode} → ${kpiCode}`);
    } catch (error: any) {
      console.error('Error deleting mapping:', error);
      alert(`Failed to delete mapping: ${error.message}`);
    }
  };

  const getIdentifierDataPairs = () => {
    // Handle multi-file scenario with per-file column selections
    if (uploadedFiles.length > 0) {
      const allPairs: any[] = [];
      
      // Get identifiers that have 100% whole mappings (should be removed from preview)
      const wholeMappedIdentifiers = loadedMappings
        .filter(mapping => mapping.mappingType === 'whole')
        .map(mapping => mapping.inputCode);
      
      // Iterate through all uploaded files using their specific column selections
      uploadedFiles.forEach(file => {
        const fileSelection = fileColumnSelections[file.name];
        if (!fileSelection || !fileSelection.identifierColumn || !fileSelection.dataColumn) {
          return; // Skip files without proper column selections
        }
        
        const identifierIndex = file.parsedData.headers.indexOf(fileSelection.identifierColumn);
        const dataIndex = file.parsedData.headers.indexOf(fileSelection.dataColumn);
        
        // If both columns exist in this file, process its rows
        if (identifierIndex !== -1 && dataIndex !== -1) {
          const filePairs = file.parsedData.rows
            .map(row => {
              const identifier = row[identifierIndex];
              const originalValue = parseFloat(row[dataIndex]) || 0;
              const percentMapped = percentMappings[identifier] || 0;
              const remainingPercent = 100 - percentMapped;
              const remainingValue = (originalValue * remainingPercent) / 100;
              
              return {
                identifier,
                value: row[dataIndex],
                originalValue,
                remainingValue: remainingValue.toFixed(2),
                percentMapped,
                remainingPercent,
                sourceFile: file.name // Track which file this data came from
              };
            })
            .filter(pair => pair.identifier && pair.identifier.toString().trim() !== '')
            .filter(pair => {
              // Remove items that have whole mappings OR have 0% remaining (100% percent mapped)
              const hasWholeMaping = wholeMappedIdentifiers.includes(pair.identifier);
              const hasRemainingPercent = pair.remainingPercent > 0;
              return !hasWholeMaping && hasRemainingPercent;
            });
          
          allPairs.push(...filePairs);
        }
      });
      
      return allPairs
        .filter(pair => {
          // Apply text filter if provided
          if (!filterText.trim()) return true;
          return pair.identifier.toLowerCase().includes(filterText.toLowerCase().trim());
        })
        .slice(0, 20); // Limit to first 20 for performance
    }
    
    // Fallback to single file logic
    if (!uploadedFile || !selectedIdentifierColumn || !selectedDataColumn) return [];
    
    const identifierIndex = uploadedFile.columns.indexOf(selectedIdentifierColumn);
    const dataIndex = uploadedFile.columns.indexOf(selectedDataColumn);
    
    if (identifierIndex === -1 || dataIndex === -1) return [];
    
    // Get identifiers that have 100% whole mappings (should be removed from preview)
    const wholeMappedIdentifiers = loadedMappings
      .filter(mapping => mapping.mappingType === 'whole')
      .map(mapping => mapping.inputCode);
    
    return uploadedFile.rows
      .map(row => {
        const identifier = row[identifierIndex];
        const originalValue = parseFloat(row[dataIndex]) || 0;
        const percentMapped = percentMappings[identifier] || 0;
        const remainingPercent = 100 - percentMapped;
        const remainingValue = (originalValue * remainingPercent) / 100;
        
        return {
          identifier,
          value: row[dataIndex],
          originalValue,
          remainingValue: remainingValue.toFixed(2),
          percentMapped,
          remainingPercent
        };
      })
      .filter(pair => pair.identifier && pair.identifier.toString().trim() !== '')
      .filter(pair => {
        // Remove items that have whole mappings OR have 0% remaining (100% percent mapped)
        const hasWholeMaping = wholeMappedIdentifiers.includes(pair.identifier);
        const hasRemainingPercent = pair.remainingPercent > 0;
        return !hasWholeMaping && hasRemainingPercent;
      })
      .filter(pair => {
        // Apply text filter if provided
        if (!filterText.trim()) return true;
        return pair.identifier.toLowerCase().includes(filterText.toLowerCase().trim());
      })
      .slice(0, 20);
  };

  const renderLeftColumn = () => {
    return (
      <div className="left-column">
        <div className="upload-step">
          <h2>
            {loading 
              ? 'Submit Data File' 
              : isMultiLocationMode
                ? `Submit Data File (Multiple Locations)`
                : accessibleLocations.length === 1 
                  ? `Submit Data File for ${accessibleLocations[0].name}`
                  : selectedLocation
                    ? `Submit Data File for ${selectedLocation.name}`
                    : accessibleLocations.length > 1
                      ? `Submit Data File (${accessibleLocations.length} locations accessible)`
                      : 'Submit Data File'
            }
          </h2>
          <p>
            Submit your financial data CSV file for processing and analysis. We'll help you ensure your data 
            meets the required format and contains all necessary information.
          </p>
          <FileUploader 
            onFileProcessed={() => {/* Don't auto-advance steps */}} 
            skipValidation={true}
          />
          
          {/* NEW: Multi-period submission configuration */}
          <SubmissionModeSelector />
          <DateRangeSelector />

        </div>
      </div>
    );
  };

  const renderRightColumn = () => {
    // Show multi-file preview if we have uploaded files, otherwise show single file or placeholder
    const hasFiles = uploadedFiles.length > 0 || uploadedFile;
    
    if (!hasFiles) {
      return (
        <div className="right-column">
          <div className="preview-placeholder">
            <div className="placeholder-content">
              <div className="placeholder-icon">📄</div>
              <h3>CSV Preview</h3>
              <p>Upload CSV files to see a preview of your data here</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="right-column">
        <div className="preview-section">
          {/* CSV Preview - Multi-file or Single file */}
          <div className="csv-preview-container">
            {uploadedFiles.length > 0 ? (
              <MultiFileCSVPreview uploadedFiles={uploadedFiles} />
            ) : uploadedFile ? (
              <CSVPreview file={uploadedFile} />
            ) : null}
          </div>
          
          {/* Action buttons with date picker */}
          <div className="preview-actions">
            {/* Date Picker */}
            <div className="date-picker-inline">
              <div className="date-picker-controls-inline">
                <div className="date-picker-group-inline">
                  <label htmlFor="year-select">Year:</label>
                  <select 
                    id="year-select"
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="date-picker-select-inline"
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="date-picker-group-inline">
                  <label htmlFor="month-select">Month:</label>
                  <select 
                    id="month-select"
                    value={selectedMonth} 
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="date-picker-select-inline"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>
                        {new Date(2024, month - 1).toLocaleString('default', { month: 'short' })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="action-buttons">
              <button className="primary-button" onClick={goToNextStep}>
                Continue to Map
              </button>
              <button className="secondary-button" onClick={handleSubmissionSummary}>
                Submission Summary
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case UPLOAD_SUBMISSION:
        return (
          <div className="two-column-layout">
            {renderLeftColumn()}
            {renderRightColumn()}
          </div>
        );
      case MAP_SUBMISSION:
        return (
          <div className="mapping-layout">
            <div className="mapping-left">
              <div className="step-header">
                <h2>Map Submission</h2>
                <button className="back-button" onClick={goToPreviousStep}>
                  Back
                </button>
              </div>
              <p>
                Configure identifier and data columns for each uploaded file to preview your data structure.
              </p>
              {(uploadedFiles.length > 0 || uploadedFile) && (
                <div className="identifier-mapping">
                  {/* File Column Configuration */}
                  <div className="column-configuration-section">
                    <div className="column-configuration-header">
                      <h3>Configure Data Columns</h3>
                      <p className="config-description">
                        {uploadedFiles.length > 1 
                          ? `Select identifier and data columns for each of your ${uploadedFiles.length} files`
                          : 'Select your identifier column (KPI Code) and data column (Value)'
                        }
                      </p>
                    </div>
                    
                    {uploadedFiles.length > 1 ? (
                      // Multi-file configuration
                      <div className="multi-file-column-config">
                        {uploadedFiles.map(file => {
                          const currentSelection = fileColumnSelections[file.name] || { identifierColumn: '', dataColumn: '' };
                          const isConfigured = currentSelection.identifierColumn && currentSelection.dataColumn;
                          
                          return (
                            <div key={file.name} className={`file-column-config ${isConfigured ? 'configured' : 'pending'}`}>
                              <div className="file-config-header">
                                <h4 className="file-name">{file.name}</h4>
                                {isConfigured && (
                                  <span className="config-status">
                                    ✓ {currentSelection.identifierColumn} → {currentSelection.dataColumn}
                                  </span>
                                )}
                              </div>
                              
                              {!isConfigured && (
                                <div className="file-column-selectors">
                                  <div className="column-selector">
                                    <label>Identifier Column:</label>
                                    <select
                                      value={currentSelection.identifierColumn}
                                      onChange={(e) => {
                                        setFileColumnSelections(prev => ({
                                          ...prev,
                                          [file.name]: {
                                            ...currentSelection,
                                            identifierColumn: e.target.value
                                          }
                                        }));
                                      }}
                                    >
                                      <option value="">Select identifier column...</option>
                                      {file.parsedData.headers.map((header, index) => (
                                        <option key={index} value={header}>
                                          {header}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  
                                  <div className="column-selector">
                                    <label>Data Column:</label>
                                    <select
                                      value={currentSelection.dataColumn}
                                      onChange={(e) => {
                                        setFileColumnSelections(prev => ({
                                          ...prev,
                                          [file.name]: {
                                            ...currentSelection,
                                            dataColumn: e.target.value
                                          }
                                        }));
                                      }}
                                    >
                                      <option value="">Select data column...</option>
                                      {file.parsedData.headers.map((header, index) => (
                                        <option key={index} value={header}>
                                          {header}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              )}
                              
                              {isConfigured && (
                                <button 
                                  type="button" 
                                  className="reconfigure-button"
                                  onClick={() => {
                                    setFileColumnSelections(prev => ({
                                      ...prev,
                                      [file.name]: { identifierColumn: '', dataColumn: '' }
                                    }));
                                  }}
                                >
                                  ✎ Reconfigure
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      // Single file configuration (fallback)
                      <div className="single-file-column-config">
                        <div className="column-selectors">
                          <div className="column-selector">
                            <label>Identifier Column (e.g., DMS Code):</label>
                            <select 
                              className="identifier-column-select"
                              value={selectedIdentifierColumn}
                              onChange={(e) => setSelectedIdentifierColumn(e.target.value)}
                            >
                              <option value="">Select identifier column...</option>
                              {(uploadedFile?.columns || []).map((column, index) => (
                                <option key={index} value={column}>
                                  {column}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="column-selector">
                            <label>Data Column (e.g., Value):</label>
                            <select 
                              className="data-column-select"
                              value={selectedDataColumn}
                              onChange={(e) => setSelectedDataColumn(e.target.value)}
                            >
                              <option value="">Select data column...</option>
                              {(uploadedFile?.columns || []).map((column, index) => (
                                <option key={index} value={column}>
                                  {column}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Data Preview */}
                  {((uploadedFiles.length > 1 && uploadedFiles.every(file => 
                    fileColumnSelections[file.name]?.identifierColumn && 
                    fileColumnSelections[file.name]?.dataColumn
                  )) || (uploadedFiles.length <= 1 && selectedIdentifierColumn && selectedDataColumn)) && (
                    <div className="data-preview-section">
                      <h3>Step 2: Preview Your Data</h3>
                      <p>Here's how your data looks with the selected columns:</p>
                      <div className="filter-section">
                        <div className="filter-input-container">
                          <input
                            type="text"
                            placeholder={`Filter by ${selectedIdentifierColumn}...`}
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            className="data-filter-input"
                          />
                          {filterText && (
                            <button
                              onClick={() => setFilterText('')}
                              className="filter-clear-btn"
                              title="Clear filter"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        {filterText && (
                          <div className="filter-results-count">
                            {getIdentifierDataPairs().length} items match your filter
                          </div>
                        )}
                      </div>
                      <div className="data-preview-table">
                        <div className="preview-header">
                          <div className="header-cell">{selectedIdentifierColumn}</div>
                          <div className="header-cell">{selectedDataColumn}</div>
                          <div className="header-cell">Remaining Percentage</div>
                          {uploadedFiles.length > 1 && <div className="header-cell">Source File</div>}
                        </div>
                        <div className="preview-rows">
                          {getIdentifierDataPairs().length === 0 ? (
                            <div className="empty-state">
                              {Object.keys(createdMappings).length > 0 
                                ? "All data has been mapped! 🎉" 
                                : "No data to display"
                              }
                            </div>
                          ) : (
                            getIdentifierDataPairs().map((pair, index) => (
                            <div 
                              key={index} 
                              className={`preview-row ${selectedInputCode === pair.identifier ? 'selected' : ''} ${selectedToggle ? 'clickable' : ''}`}
                              onClick={() => selectedToggle ? handleInputCodeSelect(pair.identifier) : null}
                            >
                              <div className="data-cell identifier-cell">
                                <strong>{pair.identifier}</strong>
                              </div>
                              <div className="data-cell value-cell">
                                {pair.value}
                              </div>
                              <div className="data-cell remaining-percentage-cell">
                                {pair.remainingPercent}%
                              </div>
                              {uploadedFiles.length > 1 && (
                                <div className="data-cell source-file-cell">
                                  {pair.sourceFile || 'Unknown'}
                                </div>
                              )}
                            </div>
                          )))}
                          {getIdentifierDataPairs().length > 0 && (() => {
                            const mappedIdentifiers = Object.values(createdMappings).flat();
                            let totalUnmappedRows = 0;
                            
                            // Count unmapped rows across all files
                            if (uploadedFiles.length > 0) {
                              uploadedFiles.forEach(file => {
                                const identifierIndex = file.parsedData.headers.indexOf(selectedIdentifierColumn);
                                if (identifierIndex !== -1) {
                                  const fileUnmappedRows = file.parsedData.rows.filter(row => {
                                    const identifier = row[identifierIndex];
                                    return identifier && !mappedIdentifiers.includes(identifier);
                                  }).length;
                                  totalUnmappedRows += fileUnmappedRows;
                                }
                              });
                            } else if (uploadedFile) {
                              // Fallback to single file
                              const identifierIndex = uploadedFile.columns.indexOf(selectedIdentifierColumn);
                              totalUnmappedRows = uploadedFile.rows.filter(row => {
                                const identifier = row[identifierIndex];
                                return identifier && !mappedIdentifiers.includes(identifier);
                              }).length;
                            }
                            
                            return totalUnmappedRows > 20 && (
                              <div className="more-data-rows">
                                +{totalUnmappedRows - 20} more unmapped rows across your files
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {!(uploadedFiles.length > 0 || uploadedFile) && (
              <div className="empty-state">No file uploaded. Please go back and upload a file.</div>
            )}
            
            <div className="mapping-middle">
              {selectedToggle && (
                <div className="mapping-status">
                  <div className="status-text">
                    {selectedInputCode 
                      ? `Selected: ${selectedInputCode} | Select a KPI →`
                      : selectedKpiCode
                        ? `Selected: ${selectedKpiCode} | ← Select data`
                        : Object.keys(createdMappings).length > 0
                          ? `${Object.values(createdMappings).flat().length} mappings created | Select more data to continue`
                          : 'Select data on left, then KPI on right'
                    }
                  </div>
                  {creatingMapping && <div className="status-creating">Creating mapping...</div>}
                </div>
              )}
              <button 
                className={`mapping-action-button ${selectedToggle === 'whole' ? 'active' : ''}`}
                type="button"
                onClick={() => setSelectedToggle('whole')}
              >
                Whole
              </button>
              <button 
                className={`mapping-action-button ${selectedToggle === 'percent' ? 'active' : ''}`}
                type="button"
                onClick={() => setSelectedToggle('percent')}
              >
                Percent
              </button>
              <button 
                className={`mapping-action-button ${manualAutoMapLoading ? 'loading' : ''}`}
                type="button"
                onClick={handleManualAutoMap}
                disabled={manualAutoMapLoading}
                title="Direct 1:1 mapping where CSV KPI codes match database KPI codes"
              >
                {manualAutoMapLoading ? 'Mapping...' : 'Manual Auto Map'}
              </button>
              <button 
                className={`mapping-action-button ${aiMappingLoading ? 'loading' : ''}`}
                type="button"
                onClick={handleAIMapping}
                disabled={aiMappingLoading}
              >
                {aiMappingLoading ? 'AI Mapping...' : 'AI Map'}
              </button>
            </div>
            
            <div className="mapping-right">
              <div className="kpi-categories-panel">
                {selectedCategoryView ? (
                  // Category Detail View
                  <>
                    <div className="category-detail-header">
                      <button 
                        className="back-button category-back-button" 
                        onClick={handleBackToCategories}
                      >
                        ← Back
                      </button>
                      <h3>{selectedCategoryView.category_name}</h3>
                    </div>
                    <div className="category-detail-kpis">
                      {categoryKPIs
                        .filter(kpi => kpi.categoryId === selectedCategoryView.id)
                        .map(kpi => (
                          <div 
                            key={kpi.id} 
                            className={`kpi-item-detail ${selectedKpiCode === kpi.kpiCode ? 'selected' : ''} ${selectedToggle ? 'clickable' : ''} ${createdMappings[kpi.kpiCode] ? 'mapped' : ''}`}
                            onClick={() => selectedToggle ? handleKpiCodeSelect(kpi.kpiCode) : null}
                          >
                            <div className="kpi-code-detail">{kpi.kpiCode}</div>
                            <div className="kpi-name-detail">{kpi.kpiName}</div>
                            {createdMappings[kpi.kpiCode] && (
                              <div className="mapping-cards">
                                {createdMappings[kpi.kpiCode].map((inputCode, index) => {
                                  // Find the mapping details to get the percentage
                                  const mappingDetails = loadedMappings?.find(m => 
                                    m.kpiCode === kpi.kpiCode && m.inputCode === inputCode
                                  );
                                  const isPercentMapping = mappingDetails?.mappingType === 'percent';
                                  const percentValue = mappingDetails?.percentValue;
                                  
                                  return (
                                    <div key={index} className="mapping-card">
                                      <span className="mapping-arrow">←</span>
                                      <span className="mapping-input-code">
                                        {inputCode}
                                        {isPercentMapping && percentValue && (
                                          <span className="mapping-percentage"> ({percentValue}%)</span>
                                        )}
                                      </span>
                                      <button 
                                        className="mapping-delete-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteMapping(kpi.kpiCode, inputCode);
                                        }}
                                        title={`Remove ${inputCode} mapping`}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </>
                ) : (
                  // Categories List View
                  <>
                    <h3>Organisation KPI Categories</h3>
                    {loadingKPIs || !kpisLoaded ? (
                      <div className="loading-state">Loading KPIs...</div>
                    ) : (
                      <div className="kpi-categories-list">
                        {kpiCategories.map(category => {
                          const isFullyMapped = isCategoryFullyMapped(category.id!);
                          return (
                            <div 
                              key={category.id} 
                              className={`kpi-category ${isFullyMapped ? 'fully-mapped' : ''}`}
                            >
                              <h4 
                                className="category-header" 
                                onClick={() => handleCategoryClick(category)}
                              >
                                {category.category_name}
                              </h4>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="mapping-actions">
                <button 
                  className={`primary-button ${!areAllCategoriesFullyMapped() ? 'disabled' : ''}`}
                  disabled={!areAllCategoriesFullyMapped()}
                  onClick={areAllCategoriesFullyMapped() ? handleSubmitData : undefined}
                >
                  Submit Data
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Submit Data</span>
          </div>

          <div className="csv-upload-container">
            <div className="stepper">
              <div className={`step ${activeStep === UPLOAD_SUBMISSION ? 'active' : ''} ${activeStep > UPLOAD_SUBMISSION ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > UPLOAD_SUBMISSION ? '✓' : '1'}</div>
                <div className="step-label">Upload Submission</div>
              </div>
              <div className={`step ${activeStep === MAP_SUBMISSION ? 'active' : ''} ${activeStep > MAP_SUBMISSION ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > MAP_SUBMISSION ? '✓' : '2'}</div>
                <div className="step-label">Map Submission</div>
              </div>

            </div>

            <div className="step-content-container">
              {renderStepContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Percent Modal */}
      {showPercentModal && pendingMapping && (() => {
        const currentMapped = percentMappings[pendingMapping.inputCode] || 0;
        const remainingPercent = 100 - currentMapped;
        
        return (
          <div className="percent-modal-overlay">
            <div className="percent-modal">
              <h3>Set Percentage Mapping</h3>
              <p>
                Mapping <strong>{pendingMapping.inputCode}</strong> to <strong>{pendingMapping.kpiCode}</strong>
              </p>
              {currentMapped > 0 && (
                <div className="current-mapping-info">
                  <span className="mapped-amount">{currentMapped}% already mapped</span>
                  <span className="remaining-amount">{remainingPercent}% remaining</span>
                </div>
              )}
              <div className="percent-input-section">
                <label htmlFor="percentValue">
                  Percentage (0-{remainingPercent}):
                </label>
                <input
                  id="percentValue"
                  type="number"
                  min="0"
                  max={remainingPercent}
                  step="0.01"
                  value={percentValue}
                  onChange={(e) => setPercentValue(e.target.value)}
                  placeholder={`Enter percentage (max ${remainingPercent}%)...`}
                  autoFocus
                />
              </div>
              <div className="percent-modal-buttons">
              <button 
                className="percent-cancel-btn" 
                onClick={handlePercentCancel}
              >
                Cancel
              </button>
              <button 
                className="percent-confirm-btn" 
                onClick={handlePercentConfirm}
                disabled={!percentValue || parseFloat(percentValue) <= 0 || parseFloat(percentValue) > remainingPercent}
              >
                Create Mapping
              </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* AI Review Modal */}
      {showAiReviewModal && (
        <div className="ai-review-modal-overlay">
          <div className="ai-review-modal">
            <div className="ai-review-header">
              <h3>AI Mapping Suggestions</h3>
              <button 
                className="ai-review-close"
                onClick={handleAiReviewClose}
              >
                ×
              </button>
            </div>
            
            <div className="ai-review-content">
              <p>
                The AI analyzed your data and found <strong>{aiSuggestions.length}</strong> confident mapping suggestions. 
                Review and select the mappings you want to create:
              </p>
              {aiSuggestions.length > 10 && (
                <p className="scroll-hint">
                  📋 <em>Scroll down to see all {aiSuggestions.length} mapping suggestions</em>
                </p>
              )}
              
              <div className="ai-suggestions-list">
                {aiSuggestions.map((suggestion, index) => {
                  const suggestionKey = `${suggestion.inputCode}-${suggestion.suggestedKpiCode}-${index}`;
                  const isSelected = selectedSuggestions[suggestionKey];
                  
                  return (
                    <div 
                      key={suggestionKey}
                      className={`ai-suggestion-item ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="suggestion-checkbox">
                        <input
                          type="checkbox"
                          id={suggestionKey}
                          checked={isSelected}
                          onChange={() => toggleSuggestionSelection(suggestionKey)}
                        />
                        <label htmlFor={suggestionKey}></label>
                      </div>
                      
                      <div className="suggestion-details">
                        <div className="suggestion-mapping">
                          <span className="input-code">{suggestion.inputCode}</span>
                          <span className="mapping-arrow">→</span>
                          <span className="kpi-info">
                            <span className="kpi-name">{getKpiNameFromCode(suggestion.suggestedKpiCode)}</span>
                            <span className="kpi-code">({suggestion.suggestedKpiCode})</span>
                          </span>
                        </div>
                        
                        <div className="suggestion-meta">
                          <span className="confidence">
                            Confidence: {suggestion.confidence}%
                          </span>
                          {uploadedFiles.length > 1 && (
                            <span className="source-files">
                              Found in: {getFilesContainingIdentifier(suggestion.inputCode).join(', ')}
                            </span>
                          )}
                          <span className="reasoning">
                            {suggestion.reasoning}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="ai-review-actions">
              <button 
                className="ai-review-cancel"
                onClick={handleAiReviewClose}
              >
                Cancel
              </button>
              <button 
                className="ai-review-confirm"
                onClick={handleCreateAIMappings}
                disabled={Object.values(selectedSuggestions).every(selected => !selected)}
              >
                Create {Object.values(selectedSuggestions).filter(selected => selected).length} AI Mappings
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SubmitDataPage;